<template>
  <div class="bbm-hub">

    <!-- Hero -->
    <section class="bbm-hub-hero">
      <div class="bbm-hub-hero__bg">
        <img
          :src="heroImage"
          :alt="actor ? actor.name : ''"
        />
        <div class="bbm-hub-hero__overlay"></div>
      </div>
      <div class="bbm-hub-hero__content">
        <div class="bbm-hub-hero__text">
          <span v-if="membershipBadge" class="bbm-hub-hero__badge">{{ membershipBadge }}</span>
          <h1 class="bbm-hub-hero__title">{{ actor ? actor.name : '' }}</h1>
          <p class="bbm-hub-hero__sub">{{ description }}</p>
          <div v-if="stats.length" class="bbm-hub-hero__stats">
            <div v-for="stat in stats" :key="stat.label" class="bbm-stat">
              <div class="bbm-stat__number">{{ stat.value }}</div>
              <div class="bbm-stat__label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Mission & Legacy -->
    <section class="bbm-section bbm-section--white">
      <div class="bbm-container">
        <div class="bbm-mission">
          <div class="bbm-mission__label-col">
            <h2 class="bbm-mission__title">Value proposition</h2>
            <div class="bbm-mission__accent-bar"></div>
          </div>
          <div class="bbm-mission__text-col">
            <p class="bbm-body-lg">{{ description }}</p>
            <div v-if="fullDescription" v-html="fullDescription" class="bbm-full-description"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Focus Areas / Tags -->
    <section v-if="tags.length" class="bbm-section">
      <div class="bbm-container">
        <div class="bbm-section__header-row">
          <h2 class="bbm-section__title">Focus Areas</h2>
        </div>
        <div class="bbm-tags-wrap">
          <span v-for="tag in tags" :key="tag.label || tag" class="bbm-focus-tag">{{ tag.label || tag }}</span>
        </div>
      </div>
    </section>

    <!-- Our Community -->
    <section class="bbm-section" v-if="isHub">
      <div class="bbm-container">
        <div class="tw-flex justify-between items-end" style="margin-bottom: 48px;">
          <div>
            <h2 class="bbm-section__title">Our Community</h2>
            <p class="bbm-section__sub">The residents shaping the future of impact innovation.</p>
          </div>
          <button class="bbm-link-btn" @click="$router.push('/actors-simplified')">VIEW ALL RESIDENTS</button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            v-for="resident in residents"
            :key="resident.name"
            class="bbm-resident-card"
          >
            <div class="tw-flex justify-between items-start" style="margin-bottom: 24px;">
              <div class="bbm-resident-card__icon-wrap">
                <span class="material-symbols-outlined bbm-resident-card__icon">{{ resident.icon }}</span>
              </div>
              <span v-if="resident.isMember" class="bbm-badge-member">BECI Member</span>
            </div>
            <h3 class="bbm-resident-card__title">{{ resident.name }}</h3>
            <p class="bbm-resident-card__desc">{{ resident.description }}</p>
            <div class="tw-flex flex-wrap gap-2">
              <span v-for="tag in resident.tags" :key="tag" class="bbm-tag">{{ tag }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Spaces & Amenities -->
    <section v-if="spaces.length" class="bbm-section bbm-section--white">
      <div class="bbm-container">
        <h2 class="bbm-section__title" style="text-align: center; margin-bottom: 48px;">Spaces for Collaboration</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div
            v-for="space in spaces"
            :key="space.name"
            class="bbm-space-card group"
          >
            <div class="bbm-space-card__image">
              <img :src="space.image" :alt="space.name"/>
              <div class="bbm-space-card__price">{{ space.price }}</div>
            </div>
            <div class="bbm-space-card__body">
              <div class="tw-flex justify-between items-center" style="margin-bottom: 16px;">
                <h3 class="bbm-space-card__title">{{ space.name }}</h3>
                <div class="tw-flex items-center gap-1 bbm-space-card__capacity">
                  <span class="material-symbols-outlined" style="font-size: 18px;">{{ space.capacityIcon }}</span>
                  {{ space.capacity }}
                </div>
              </div>
              <p class="bbm-space-card__desc">{{ space.description }}</p>
              <button class="bbm-space-card__btn" @click="$router.push('/login')">Book Now</button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact & Map -->
    <section class="bbm-contact">
      <div class="bbm-contact__info">
        <h2 class="bbm-contact__title">Get in Touch</h2>
        <div v-if="contactItems.length" class="bbm-contact__items">
          <div v-for="item in contactItems" :key="item.label" class="bbm-contact__item">
            <div class="bbm-contact__item-icon">
              <span class="material-symbols-outlined">{{ item.icon }}</span>
            </div>
            <div>
              <div class="bbm-contact__item-label">{{ item.label }}</div>
              <a
                v-if="item.type === 'link'"
                :href="item.href"
                target="_blank"
                rel="noopener noreferrer"
                class="bbm-contact__item-value bbm-contact__item-link"
              >{{ item.value }}</a>
              <button
                v-else-if="item.type === 'login'"
                class="bbm-contact__login-cta"
                @click="goToLogin(actor)"
              >
                <span class="material-symbols-outlined" style="font-size: 14px;">lock</span>
                Login to view
              </button>
            </div>
          </div>
        </div>
        <div v-else class="bbm-contact__empty">
          <span class="material-symbols-outlined bbm-contact__empty-icon">location_city</span>
          <p class="bbm-contact__empty-text">Contact details are not yet available for this hub.</p>
          <button class="bbm-contact__login-cta" @click="goToLogin(actor)">
            <span class="material-symbols-outlined" style="font-size: 14px;">lock</span>
            Login to see full profile
          </button>
        </div>
      </div>
      <div class="bbm-contact__map">
        <div ref="leafletMap" class="bbm-contact__map-frame"></div>
      </div>
    </section>

    <!-- CTA -->
    <section class="bbm-cta">
      <div class="bbm-cta__accent"></div>
      <div class="bbm-container" style="position: relative; z-index: 1; text-align: center;">
        <h2 class="bbm-cta__title">Ready to Scale Your Impact?</h2>
        <p class="bbm-cta__sub">
          Join a community of 250+ founders and organizations dedicated to building a better future
          through technology and innovation.
        </p>
        <button class="bbm-cta__btn" @click="$router.push('/register')">Get in Touch</button>
      </div>
    </section>

    <SimplifiedFooterBrussels/>

  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { fetchActor } from '@/api/actors'
  import { getDefaultFallbackImageUrl } from '@/util/helpers'
  import SimplifiedFooterBrussels from '@/components/SimplifiedFooter/SimplifiedFooterBrussels.vue'
  import L from 'leaflet'
  import 'leaflet/dist/leaflet.css'

  export default defineComponent({
    name: 'StaticHomeBrusselsHubs',
    data() {
      return {
        actor: null as any,
        residents: [] as any[],
        spaces: [] as any[],
        _leafletMap: null as any,
      }
    },
    computed: {
      isHub(): boolean {
        return false
      },
      heroImage(): string {
        if (!this.actor) return getDefaultFallbackImageUrl()
        return this.actor.featured_image_url || this.actor.website_screenshot || getDefaultFallbackImageUrl()
      },
      stats(): { value: string; label: string }[] {
        const items: { value: string; label: string }[] = []
        const foundingDate = this.actor?.founding_date
        if (foundingDate) {
          const year = String(foundingDate).split('-')[0]
          if (year) items.push({ value: year, label: 'FOUNDED IN' })
        }
        const sizeRange = this.actor?.current_employees?.[0]
        if (sizeRange) {
          const lo = sizeRange.lower_bound
          const hi = sizeRange.upper_bound
          const label = lo === hi ? `${lo}` : `${lo}–${hi}`
          items.push({ value: label, label: 'TEAM SIZE' })
        } else {
          const employeeCount = this.actor?.employees?.length
          if (employeeCount > 0) {
            items.push({ value: `${employeeCount}+`, label: 'EMPLOYEES' })
          }
        }
        const funding = this.actor?.total_funding || this.actor?.original_total_funding
        const currency = this.actor?.total_funding_currency || this.actor?.original_total_funding_currency
        if (funding) {
          items.push({ value: this.formatFunding(funding, currency), label: 'FUNDS RAISED' })
        }
        return items
      },
      tags(): any[] {
        return this.actor?.tags || []
      },
      fullDescription(): string {
        if (!this.actor) return ''
        const full = this.actor.description || ''
        const short = this.description
        // Only show full description if it's meaningfully longer / different from the short one
        if (!full || full === short || full.replace(/<[^>]+>/g, '').trim() === short.trim()) return ''
        return full
      },
      membershipBadge(): string {
        const memberships = this.actor?.membership
        if (!memberships?.length) return ''
        const label = memberships[0].label?.toUpperCase()
        return ['HUB', 'PARTNER', 'MEMBER'].includes(label) ? label : ''
      },
      description(): string {
        if (!this.actor) return ''
        return this.actor.short_description || this.actor.activities_description || ''
      },
      contactItems(): any[] {
        if (!this.actor) return []
        const items: any[] = []
        const addr = this.actor.address
        if (addr?.street || addr?.city) {
          const parts = [addr.street, [addr.zip, addr.city].filter(Boolean).join(' '), addr.country].filter(Boolean)
          items.push({ icon: 'location_on', label: 'ADDRESS', type: 'link', href: `https://maps.google.com/?q=${encodeURIComponent(parts.join(', '))}`, value: parts.join(', ') })
        }
        if (this.actor.website) {
          items.push({ icon: 'language', label: 'URL', type: 'link', href: this.actor.website, value: this.actor.website.replace(/^https?:\/\//, '') })
        }
        if (this.actor.linkedin) {
          items.push({ icon: 'open_in_new', label: 'LINKEDIN', type: 'link', href: this.actor.linkedin, value: 'View on LinkedIn' })
        }
        const twitter = Array.isArray(this.actor.twitter) ? this.actor.twitter[0]?.url : this.actor.twitter
        if (twitter) {
          items.push({ icon: 'open_in_new', label: 'TWITTER / X', type: 'link', href: twitter, value: 'View on X' })
        }
        const instagram = Array.isArray(this.actor.instagram) ? this.actor.instagram[0]?.url : this.actor.instagram
        if (instagram) {
          items.push({ icon: 'open_in_new', label: 'INSTAGRAM', type: 'link', href: instagram, value: 'View on Instagram' })
        }
        const youtube = Array.isArray(this.actor.youtube) ? this.actor.youtube[0]?.url : this.actor.youtube
        if (youtube) {
          items.push({ icon: 'smart_display', label: 'YOUTUBE', type: 'link', href: youtube, value: 'View on YouTube' })
        }
        const facebook = Array.isArray(this.actor.facebook) ? this.actor.facebook[0]?.url : this.actor.facebook
        if (facebook) {
          items.push({ icon: 'open_in_new', label: 'FACEBOOK', type: 'link', href: facebook, value: 'View on Facebook' })
        }
        if (this.actor.email) {
          items.push({ icon: 'mail', label: 'EMAIL', type: 'login' })
        }
        if (this.actor.phone) {
          items.push({ icon: 'call', label: 'PHONE', type: 'login' })
        }
        return items
      },
    },
    methods: {
      goToLogin(actor: any) {
        if (this.$store.getters.isLoggedIn) {
          this.$router.push(`/actors/${actor?.id}`)
        } else {
          this.$router.push({ path: '/login', query: { external: 'view-actor', actor_id: actor?.id, actor_name: actor?.name } })
        }
      },
      initMap() {
        const el = this.$refs.leafletMap as HTMLElement
        if (!el) return

        const lat = this.actor?.location?.lat ?? 50.8503
        const lon = this.actor?.location?.lng ?? this.actor?.location?.lon ?? 4.3517

        // Fix default marker icon paths broken by webpack
        delete (L.Icon.Default.prototype as any)._getIconUrl
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        })

        const map = L.map(el, {
          center: [lat, lon],
          zoom: 15,
          scrollWheelZoom: false,
          zoomControl: true,
        })

        L.tileLayer('https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png', {
          attribution: 'powered by <a href="https://carto.com">Carto</a>',
          attributionControl: false,
          maxZoom: 19,
        }).addTo(map)

        L.marker([lat, lon])
          .addTo(map)
          .bindPopup(this.actor?.name || '')

        this._leafletMap = map
      },
      formatFunding(amount: number, currency: string): string {
        const symbols: Record<string, string> = { eur: '€', usd: '$', gbp: '£', chf: 'CHF ' }
        const symbol = symbols[currency?.toLowerCase()] ?? ''
        if (amount >= 1_000_000_000) return `${symbol}${(amount / 1_000_000_000).toFixed(1)}B`
        if (amount >= 1_000_000) return `${symbol}${Math.round(amount / 1_000_000)}M`
        if (amount >= 1_000) return `${symbol}${Math.round(amount / 1_000)}K`
        return `${symbol}${amount}`
      },
    },
    components: { SimplifiedFooterBrussels },
    async mounted() {
      const id = this.$route.params.id as string
      this.actor = await fetchActor(id)
      await this.$nextTick()
      this.initMap()
    },
    unmounted() {
      if (this._leafletMap) {
        this._leafletMap.remove()
        this._leafletMap = null
      }
    },
  })
</script>

<!-- Leaflet CSS is imported globally via <script>. Contain its high z-indexes so they don't bleed over other page chrome. -->
<style>
  .leaflet-container {
    isolation: isolate;
  }
</style>

<style scoped lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap');

  $bbm-primary: var(--primary-community);
  $bbm-primary-container: var(--primary-community);
  $bbm-accent: #ff6f31;
  $bbm-secondary-container: #00ccf9;
  $bbm-on-surface-variant: #43474f;
  $bbm-surface: #f7f9fb;
  $bbm-surface-container: #eceef0;
  $bbm-outline-variant: #c3c6d1;

  .material-symbols-outlined {
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }

  .bbm-hub {
    font-family: 'Inter', sans-serif;
    background: $bbm-surface;
    color: #191c1e;
    overflow-x: hidden;
  }

  // Hero
  .bbm-hub-hero {
    position: relative;
    height: 716px;
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .bbm-hub-hero__bg {
    position: absolute;
    inset: 0;
    z-index: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .bbm-hub-hero__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 30, 64, 0.62);
    backdrop-filter: blur(2px);
  }

  .bbm-hub-hero__content {
    position: relative;
    z-index: 1;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 40px;
    width: 100%;
  }

  .bbm-hub-hero__text {
    max-width: 640px;
    color: white;
  }

  .bbm-hub-hero__badge {
    display: inline-block;
    padding: 4px 12px;
    background: $bbm-secondary-container;
    color: #005266;
    font-family: 'Inter', sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 16px;
  }

  .bbm-hub-hero__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(2.5rem, 5vw, 3rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: white;
    margin: 0 0 24px;
  }

  .bbm-hub-hero__sub {
    font-size: 1.125rem;
    line-height: 1.6;
    color: #cbd5e1;
    margin: 0 0 40px;
  }

  .bbm-hub-hero__stats {
    display: flex;
    gap: 48px;
    flex-wrap: wrap;
  }

  .bbm-stat__number {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 2.5rem;
    font-weight: 700;
    line-height: 1;
    color: $bbm-secondary-container;
  }

  .bbm-stat__label {
    font-family: 'Inter', sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #94a3b8;
    margin-top: 4px;
  }

  // Sections
  .bbm-section {
    padding: 80px 0;
  }

  .bbm-section--white {
    background: white;
  }

  .bbm-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 40px;
  }

  .bbm-section__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 2rem;
    font-weight: 600;
    color: $bbm-primary-container;
    margin: 0;
    line-height: 1.2;
  }

  .bbm-section__sub {
    font-size: 1rem;
    color: $bbm-on-surface-variant;
    margin: 8px 0 0;
  }

  .bbm-body-lg {
    font-size: 1.125rem;
    line-height: 1.6;
    color: $bbm-on-surface-variant;
    margin: 0;
  }

  // Mission
  .bbm-mission {
    display: grid;
    grid-template-columns: 5fr 7fr;
    gap: 24px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .bbm-mission__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 2rem;
    font-weight: 600;
    color: $bbm-primary-container;
    margin: 0 0 24px;
    line-height: 1.2;
  }

  .bbm-mission__accent-bar {
    width: 80px;
    height: 4px;
    background: $bbm-accent;
  }

  // Full HTML description
  .bbm-full-description {
    margin-top: 24px;
    font-size: 1rem;
    line-height: 1.7;
    color: $bbm-on-surface-variant;

    p { margin: 0 0 16px; }
    ul, ol { margin: 0 0 16px; padding-left: 24px; }
    a { color: $bbm-primary-container; text-decoration: underline; }
    strong { font-weight: 600; }
  }

  // Section header row
  .bbm-section__header-row {
    margin-bottom: 32px;
  }

  // Focus tags
  .bbm-tags-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .bbm-focus-tag {
    display: inline-block;
    padding: 8px 18px;
    background: white;
    border: 1px solid $bbm-outline-variant;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    color: $bbm-on-surface-variant;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    &:hover {
      background: $bbm-primary-container;
      border-color: $bbm-primary-container;
      color: white;
    }
  }

  // Resident cards
  .bbm-resident-card {
    background: white;
    padding: 32px;
    border: 1px solid $bbm-outline-variant;
    transition: box-shadow 0.25s, transform 0.25s;

    &:hover {
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
      transform: translateY(-4px);
    }
  }

  .bbm-resident-card__icon-wrap {
    width: 64px;
    height: 64px;
    background: $bbm-surface-container;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .bbm-resident-card__icon {
    color: $bbm-primary-container;
    font-size: 2.25rem;
  }

  .bbm-resident-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: $bbm-primary-container;
    margin: 0 0 12px;
  }

  .bbm-resident-card__desc {
    font-size: 1rem;
    line-height: 1.5;
    color: $bbm-on-surface-variant;
    margin: 0 0 24px;
  }

  .bbm-badge-member {
    display: inline-block;
    background: #eff6ff;
    color: #1d4ed8;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    padding: 4px 8px;
    white-space: nowrap;
  }

  .bbm-tag {
    display: inline-block;
    background: $bbm-surface-container;
    color: $bbm-on-surface-variant;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 4px 12px;
  }

  // Link button
  .bbm-link-btn {
    font-family: 'Inter', sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: $bbm-primary-container;
    background: none;
    border: none;
    border-bottom: 2px solid $bbm-primary-container;
    padding-bottom: 4px;
    cursor: pointer;
    transition: color 0.15s, border-color 0.15s;
    white-space: nowrap;

    &:hover {
      color: $bbm-accent;
      border-color: $bbm-accent;
    }
  }

  // Spaces
  .bbm-space-card {
    background: white;
    overflow: hidden;
  }

  .bbm-space-card__image {
    position: relative;
    height: 320px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s;

      .bbm-space-card:hover & {
        transform: scale(1.08);
      }
    }
  }

  .bbm-space-card__price {
    position: absolute;
    bottom: 16px;
    left: 16px;
    background: $bbm-primary-container;
    color: white;
    padding: 8px 16px;
    font-weight: 700;
    font-size: 0.9375rem;
  }

  .bbm-space-card__body {
    padding: 32px;
    border: 1px solid $bbm-outline-variant;
    border-top: none;
  }

  .bbm-space-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: $bbm-primary-container;
    margin: 0;
  }

  .bbm-space-card__capacity {
    font-size: 0.9375rem;
    color: $bbm-on-surface-variant;
  }

  .bbm-space-card__desc {
    font-size: 1rem;
    line-height: 1.5;
    color: $bbm-on-surface-variant;
    margin: 0 0 24px;
  }

  .bbm-space-card__btn {
    width: 100%;
    padding: 12px 0;
    background: transparent;
    color: $bbm-primary-container;
    border: 2px solid $bbm-primary-container;
    font-weight: 700;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: $bbm-primary-container;
      color: white;
    }
  }

  // Contact & Map
  .bbm-contact {
    display: flex;
    flex-direction: column;
    height: auto;
    background: $bbm-surface;

    @media (min-width: 768px) {
      flex-direction: row;
      height: 600px;
    }
  }

  .bbm-contact__info {
    padding: 80px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (min-width: 768px) {
      width: 50%;
    }

    @media (max-width: 768px) {
      padding: 48px 24px;
    }
  }

  .bbm-contact__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 2rem;
    font-weight: 600;
    color: $bbm-primary-container;
    margin: 0 0 40px;
  }

  .bbm-contact__items {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  .bbm-contact__item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  .bbm-contact__item-icon {
    width: 48px;
    height: 48px;
    background: $bbm-primary-container;
    color: white;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .bbm-contact__item-label {
    font-family: 'Inter', sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: $bbm-on-surface-variant;
    margin-bottom: 4px;
  }

  .bbm-contact__item-value {
    font-size: 1.125rem;
    line-height: 1.6;
    color: $bbm-primary-container;
    font-weight: 600;
  }

  .bbm-contact__item-link {
    text-decoration: none;
    font-size: 1.125rem;
    font-weight: 600;
    color: $bbm-primary-container;
    transition: color 0.15s;

    &:hover {
      color: $bbm-accent;
    }
  }

  .bbm-contact__login-cta {
    display: inline-flex;
    width: fit-content;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    padding: 6px 14px;
    background: transparent;
    border: 1.5px solid $bbm-primary-container;
    border-radius: 6px;
    color: $bbm-primary-container;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: $bbm-primary-container;
      color: white;
    }
  }

  .bbm-contact__map {
    position: relative;
    background: $bbm-surface-container;
    overflow: hidden;

    @media (min-width: 768px) {
      width: 50%;
    }

    @media (max-width: 768px) {
      height: 300px;
    }
  }

  .bbm-contact__map-frame {
    width: 100%;
    height: 100%;
    min-height: 300px;
    display: block;
  }

  .bbm-contact__empty {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 8px;
  }

  .bbm-contact__empty-icon {
    font-size: 2rem;
    color: #cbd5e1;
  }

  .bbm-contact__empty-text {
    font-size: 0.95rem;
    color: #94a3b8;
    margin: 0;
  }

  // CTA
  .bbm-cta {
    background: $bbm-primary-container;
    padding: 80px 0;
    position: relative;
    overflow: hidden;
    text-align: center;
  }

  .bbm-cta__accent {
    position: absolute;
    top: 0;
    right: 0;
    width: 33%;
    height: 100%;
    background: $bbm-accent;
    opacity: 0.1;
    transform: skewX(12deg) translateX(50%);
    pointer-events: none;
  }

  .bbm-cta__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 2rem;
    font-weight: 600;
    color: white;
    margin: 0 0 24px;
  }

  .bbm-cta__sub {
    font-size: 1.125rem;
    line-height: 1.6;
    color: #cbd5e1;
    max-width: 600px;
    margin: 0 auto 40px;
  }

  .bbm-cta__btn {
    padding: 16px 40px;
    background: $bbm-accent;
    color: white;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.1rem;
    font-weight: 700;
    border: none;
    cursor: pointer;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s, filter 0.2s;

    &:hover {
      transform: scale(1.04);
      filter: brightness(1.1);
    }
  }


  @media (max-width: 768px) {
    .bbm-hub-hero {
      height: auto;
      padding: 80px 0 60px;
    }

    .bbm-hub-hero__content {
      padding: 0 20px;
    }

    .bbm-hub-hero__stats {
      gap: 24px;
    }

    .bbm-container {
      padding: 0 20px;
    }
  }
</style>
